import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom, Observable } from 'rxjs';
import { ContextService } from './context.service';
import { UnitService } from './unit.service';
import { LanguageService } from '../../shared/services/language.service';
import { PaymentV2Response } from '../models/cateringOrder.model';
import {
  getAddonSelectionsTotal,
  normalizeCartItemAddons,
} from '../utils/cart-addon.util';
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

/** Saved with checkout draft when delivery is selected — restores address, invoice fields, and corporate UI. */
export interface CheckoutDraftDeliveryInformation {
  deliveryLocation: Record<string, unknown>;
  corporateCompanyKey?: string | null;
  corporateOrganizationId?: string | null;
  corporateLocationId?: string | null;
  corporateSubLocationId?: string | null;
}

export interface CheckoutDraftData {
  selectedDate: string | null;
  selectedTimeSlot: any | null;
  selectedTime: string | null;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  deliveryInformation?: CheckoutDraftDeliveryInformation | null;
  deliveryCostCenter?: string | null;
  commentToRestaurant?: string | null;
  /** Guest invoice checkout fields; absent for every other flow. */
  guestInvoice?: GuestInvoiceDraft | null;
}

/** Guest "order by invoice" fields persisted with the checkout draft. */
export interface GuestInvoiceDraft {
  isGuestInvoice: boolean;
  meetingRoom: string;
  eventHost: string;
  numberOfAttendees: number | null;
  specialDiets: string;
  invoiceReference: string;
  eventEndTime: string | null;
  meetingPurpose: string;
  participants: string;
  company: {
    connectedCompany: string;
    organizationNumber: string;
    vatNumber: string;
    invoiceEmail: string;
    billingStreet: string;
    billingPostalCode: string;
    billingCity: string;
    costCenter: string;
  };
}

type CheckoutDraftEnvelope = {
  updatedAt: number;
  data: CheckoutDraftData;
};

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private readonly CHECKOUT_DRAFT_PREFIX = 'checkoutDraft:';
  private readonly CHECKOUT_DRAFT_MAX_AGE_MS = 2 * 60 * 60 * 1000;
  private readonly hasSessionStorage = typeof sessionStorage !== 'undefined';
  private readonly ORDER_STATUSES = {
    active: ['Pending', 'approved'],
    previous: ['completed', 'declined'],
  } as const;

  private orderUnsubscribes: Record<'active' | 'previous', (() => void) | null> = {
    active: null,
    previous: null,
  };

  readonly activeOrders = signal<any[]>([]);
  readonly previousOrders = signal<any[]>([]);
  readonly activeOrdersLoading = signal<boolean>(false);
  readonly previousOrdersLoading = signal<boolean>(false);
  readonly receipts = signal<any[]>([]);
  readonly receiptsLoaded = signal(false);
  readonly receiptsLoading = signal<boolean>(false);

  private http = inject(HttpClient);
  routeName = signal<string>('');
  retryingEnabled = signal<boolean>(false);
  paymentMethods = ['card', 'mobilepay', 'googlepay', 'applepay', 'swish', 'invoice'];
  private unitService = inject(UnitService);
  private languageService = inject(LanguageService);
  constructor(private contextService: ContextService) {
    this.routeName.set(this.unitService.routeName());
  }

  private getCheckoutDraftKey(routeName: string): string {
    const route = routeName || this.unitService.routeName() || 'default';
    return `${this.CHECKOUT_DRAFT_PREFIX}${route}`;
  }

  saveCheckoutDraft(routeName: string, draft: CheckoutDraftData): void {
    if (!this.hasSessionStorage) return;
    const payload: CheckoutDraftEnvelope = { updatedAt: Date.now(), data: draft };
    sessionStorage.setItem(this.getCheckoutDraftKey(routeName), JSON.stringify(payload));
  }

  getCheckoutDraft(routeName: string): CheckoutDraftData | null {
    if (!this.hasSessionStorage) return null;
    const key = this.getCheckoutDraftKey(routeName);
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as CheckoutDraftEnvelope;
      const isFresh =
        parsed?.data &&
        parsed.updatedAt > 0 &&
        Date.now() - parsed.updatedAt <= this.CHECKOUT_DRAFT_MAX_AGE_MS;

      if (!isFresh) {
        sessionStorage.removeItem(key);
        return null;
      }
      return parsed.data;
    } catch {
      sessionStorage.removeItem(key);
      return null;
    }
  }

  clearCheckoutDraft(routeName: string): void {
    if (!this.hasSessionStorage) return;
    sessionStorage.removeItem(this.getCheckoutDraftKey(routeName));
  }

  private getLastMonthDateRange(): { startISO: string; endISO: string; startSeconds: number; endSeconds: number } {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    const normalizedStart = new Date(startDate);
    normalizedStart.setHours(0, 0, 0, 0);
    const normalizedEnd = new Date(endDate);
    normalizedEnd.setHours(23, 59, 59, 999);

    return {
      startISO: normalizedStart.toISOString(),
      endISO: normalizedEnd.toISOString(),
      startSeconds: Math.floor(normalizedStart.getTime() / 1000),
      endSeconds: Math.floor(normalizedEnd.getTime() / 1000),
    };
  }

  private generateIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      // Generate UUID and remove one character to get 35 characters
      return crypto.randomUUID().slice(0, -1);
    }
    // Fallback for environments without crypto.randomUUID
    return 'xxxxxxxxxxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private generateOrderCode(firstName: string, lastName: string, uid: string): string {
    const fn = (firstName ?? '').trim();
    const ln = (lastName ?? '').trim();
    const safeUid = (uid ?? '').trim();
    let orderCode: string;

    if (!fn && !ln) {
      orderCode = safeUid.substring(0, 4);
    } else if (fn && !ln) {
      orderCode = fn.substring(0, 2) + safeUid.substring(0, 2);
    } else {
      orderCode = fn.substring(0, 2) + ln.substring(0, 2);
    }

    if (orderCode.toLowerCase() === 'anal') orderCode = 'lana';
    return orderCode.toUpperCase();
  }

  private mapProductsToFoodArray(products: any[]): any[] {
    return products.map((item) => {
      const originalCost = item.product.originalCost ?? item.originalCost ?? item.product.cost;
      const product = item.product || item;
      const quantity = item.quantity || 1;
      const selectedAddons = normalizeCartItemAddons(item.selectedAddons);
      const addonTotal = getAddonSelectionsTotal(selectedAddons, quantity);
      const lineTotalBeforeDiscount = originalCost * quantity + addonTotal;
      // External ID is optional on catering products; send '' so sales exports never see undefined.
      const externalId = product?.externalOrigin?.externalId || '';
      const discountValue = Number(item?.discountValue || 0);
      const discountedLineTotal = typeof item?.totalPrice === 'number'
        ? item.totalPrice
        : Math.round(lineTotalBeforeDiscount - discountValue);
      return {
        Cost: originalCost,
        discountValue,
        discountPercent: Number(item?.discountPercent || 0),
        totalPrice: discountedLineTotal,
        priceOfOptions: addonTotal,
        selectedAddons,
        Category:
          typeof product?.category === 'string'
            ? product.category
            : product?.category?.name || '',
        Description: product?.description || '',
        DescriptionLong: product?.descriptionLong || '',
        ImageUrl: product?.imageUrl,
        Name: product?.name,
        basePrice: originalCost,
        dateCreated: product?.createdDate || new Date().toISOString(),
        dateEdited: product?.lastEdited || new Date().toISOString(),
        externalId: externalId,
        externalOrigin: {
          externalId: externalId,
          key: 'TCPOS',
        },
        key: product?.key,
        stockBalance: product?.stockBalance ?? 0,
        useStockBalance: product?.useStockBalance ?? false,
        vatInfo: {
          articleAccountId: product?.vatInfo?.articleAccountId,
        },
        type: product?.type || null,
        NrOfItems: quantity,
      };
    });
  }

  private buildPaymentSource(paymentMethod: string): any {
    const method = paymentMethod?.toLowerCase()?.trim();

    if (method === 'card') {
      return {
        nickname:
          'newcard this should not be needed lets remove this in the future, ok?',
        recurringReference: null,
        type: 'newcard',
      };
    } else if (method === 'mobilepay') {
      return {
        nickname:
          'Mobile Pay',
        recurringReference: 'mobilepay',
        type: 'newcard',
      };
    } else if (method === 'googlepay') {
      return {
        nickname: 'Google Pay',
        recurringReference: 'googlepay',
        type: 'newcard',
      };
    } else if (method === 'applepay') {
      return {
        nickname: 'Apple Pay',
        recurringReference: 'applepay',
        type: 'newcard',
      };
    } else if (method === 'swish') {
      return {
        nickname: 'Swish',
        recurringReference: null,
        type: 'swish',
      };
    } else if (method === 'invoice') {
      return {
        nickname: 'Invoice',
        recurringReference: null,
        type: 'invoice',
      };
    }
    return {
      nickname:
        'newcard this should not be needed lets remove this in the future, ok?',
      recurringReference: null,
      type: 'newcard',
    };
  }

  private buildProviderData(paymentSource: any): any {
    if (paymentSource?.type === 'invoice') {
      return {
        type: 'invoice',
        paymentSource: paymentSource,
        provider: 'invoice',
        customer: null,
      };
    }
    const paymentProvider = this.unitService.paymentProvider();
    const normalizedProvider = paymentProvider?.toLowerCase()?.trim() || 'stripe';
    const validProvider = (normalizedProvider === 'stripe' || normalizedProvider === 'reepay')
      ? normalizedProvider
      : 'swish';

    return {
      type: paymentSource?.type === 'swish' ? null : 'newcard',
      paymentSource: paymentSource,
      provider: validProvider,
      customer: null,
    };
  }

  private buildPaymentPayload(
    orderDetails: any,
    orderId: string,
    idempotencyKey: string,
    client: any
  ): any {
    const now = Date.now();
    const dateISO = new Date(now).toISOString();

    const mappedItems = this.mapProductsToFoodArray(orderDetails.products || []);

    const foodArray = mappedItems.filter((item) => {
      const typeId = item.type?.productTypeId;
      if (!typeId) return true;
      return String(typeId) !== '2';
    });

    const drinksArray = mappedItems.filter((item) => {
      const typeId = item.type?.productTypeId;
      if (!typeId) return false;
      return String(typeId) === '2';
    });

    const totalFoodCost = foodArray.reduce(
      (sum, item) => sum + (item.Cost * (item.NrOfItems || 0) + (item.priceOfOptions || 0)),
      0
    );

    const totalDrinksCost = drinksArray.reduce(
      (sum, item) => sum + (item.Cost * (item.NrOfItems || 0) + (item.priceOfOptions || 0)),
      0
    );

    const totalFoodItems = foodArray.reduce(
      (sum, item) => sum + (item.NrOfItems || 0),
      0
    );

    const totalDrinkItems = drinksArray.reduce(
      (sum, item) => sum + (item.NrOfItems || 0),
      0
    );

    const routeName = client?.routeName;
    const paymentMethod = orderDetails.paymentMethod || 'stripe';
    const customerFirstName = orderDetails.customer?.firstName ?? '';
    const customerLastName = orderDetails.customer?.lastName ?? '';
    const userIdentifier = String(orderDetails.userId ?? orderDetails.customer?.id ?? orderId);
    const orderCode = this.generateOrderCode(customerFirstName, customerLastName, userIdentifier);

    // Build payment source object once and reuse it
    const paymentSource = this.buildPaymentSource(paymentMethod);
    const providerData = this.buildProviderData(paymentSource);

    return {
      idempotencyKey: idempotencyKey,
      receiptOnly: true, // It should true when there is no user ( Not logged in )
      order: {
        version: '1',
        appType: 'cateringWeb',
        client: client,
        date: Math.floor(now / 1000),
        dateISO: dateISO,
        totalCost: orderDetails.totalCost ?? orderDetails.totalPrice ?? 0,
        totalFoodCost: totalFoodCost,
        totalDrinksCost: totalDrinksCost,
        totalFoodItems: totalFoodItems,
        totalDrinkItems: totalDrinkItems,
        totalItems: totalFoodItems + totalDrinkItems,
        order: {
          food: foodArray,
          drinks: drinksArray,
        },
        orderType: 'catering',
        userName: orderDetails.customer
          ? `${orderDetails.customer.firstName || ''} ${orderDetails.customer.lastName || ''
            }`.trim()
          : '',
        phoneNumber: orderDetails.customer?.phone,
        orderCode: orderCode,
        isDelivery: orderDetails.isDelivery,
        status: 'Pending',
        Deliverd: false,
        timeslots: orderDetails.timeSlots ? orderDetails.timeSlots : null,
        commentToRestaurant: orderDetails.commentToRestaurant,
        meetingPurpose: orderDetails.meetingPurpose ?? null,
        participants: orderDetails.participants ?? null,
        // Guest invoice checkout. Verified to persist through payments2 to
        // Firestore pos/{routeName}/catering_orders unchanged.
        ...(orderDetails.orderSource
          ? {
              orderSource: orderDetails.orderSource,
              isPaid: false,
              paymentMethod: orderDetails.paymentMethod,
              eventHost: orderDetails.eventHost ?? null,
              numberOfAttendees: orderDetails.numberOfAttendees ?? null,
              specialDiets: orderDetails.specialDiets ?? null,
              invoiceReference: orderDetails.invoiceReference ?? null,
              eventEndTime: orderDetails.eventEndTime ?? null,
            }
          : {}),
        deliveryLocation: orderDetails.deliveryLocation,
        customer: orderDetails.customer,
        restaurantInfo: orderDetails.restaurantInfo,
        pickupOrDeliveryTime: orderDetails.pickupOrDeliveryTime,
        orderNumber: orderDetails.orderNumber,
        id: orderId,
        userId: orderDetails.userId,
        ...(orderDetails.coupon ? { coupon: orderDetails.coupon } : {}),
        ...(orderDetails.totalDiscount ? { totalDiscount: orderDetails.totalDiscount } : {}),
      },
      paymentType: paymentSource?.type,
      appVersion: 'devclient',
      userPaymentSource: paymentSource,
      clientContext: {
        user: {
          isAnonymous: false,
          firstName: orderDetails.customer?.firstName,
          address: orderDetails.customer?.address,
          city: orderDetails.customer?.city,
          postalCode: orderDetails.customer?.postalCode,
          customerLocale: orderDetails.customer?.locale,
          lastName: orderDetails.customer?.lastName,
          deviceToken: null,
          email: orderDetails.customer?.email,
          phoneNumber: orderDetails.customer?.phone,
          preferredLocale: this.languageService.currentLanguage || client?.preferredLocale || 'en',
          origin: routeName,
        },
        origin: 'catering',
        locale: this.languageService.currentLanguage || client?.locale || 'en',
        appVersion: 'devclient',
      },
      providerData: providerData,
    };
  }

  async proceedPayment(orderDetails: any): Promise<any> {
    try {
      const client = this.contextService.getClientValue();

      let clientData = client;
      if (!clientData) {
        clientData = await this.contextService.getClient(this.unitService.routeName());
        clientData.routeName = this.unitService.routeName();
      }
      const idempotencyKey = this.generateIdempotencyKey();
      const orderId = `catering_or_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      const paymentMethod = (orderDetails.paymentMethod || '')
        .toLowerCase()
        .trim();
      if (
        this.paymentMethods.includes(paymentMethod)
      ) {
        const requestBody = this.buildPaymentPayload(
          orderDetails,
          orderId,
          idempotencyKey,
          clientData
        );

        const apiEndpoint = `${environment.pubQueuePaymentApiUrl}/v1/idempotency/initiate`;
        const response = await firstValueFrom(
          this.http.post<any>(apiEndpoint, requestBody)
        );
        if (response && response.success) {
          const checkoutUrl = response.providerData?.url;
          return {
            ...response,
            checkoutUrl: checkoutUrl,
          };
        } else if (response && response.error) {
          return response;
        } else {
          throw new Error('Error while initiating the payment session.');
        }
      } else {
        return;
      }
    } catch (error: any) {
      throw new Error('Error while initiating the payment session.');
    }
  }

  async getQrCode(token: string): Promise<string> {
    try {
      const body = {
        token,
        format: 'png',
        size: 300
      };

      const url = `${environment.pubQueuePaymentApiUrl}/v1/swish/qr`;
      const image: any = await this.http.post(url, body, { responseType: 'blob' }).toPromise();

      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          resolve(reader.result as string);
        };

        reader.onerror = () => {
          reject(new Error('Failed to read QR code image.'));
        };

        // Transform the file into a data URL
        reader.readAsDataURL(image as Blob);
      });
    } catch (error: any) {
      console.error('Error while generating QR code', error);
      throw new Error('Error while generating QR code.');
    }
  }

  async verifyPaymentWithRetry(idempotencyKey: string, retryMaxCount: number): Promise<PaymentV2Response> {
    this.retryingEnabled.set(true);
    let resp: PaymentV2Response = new PaymentV2Response();
    let finished = false;
    let _retryMaxCount: number = retryMaxCount >= 0 ? retryMaxCount : 120;
    let retry = 0;
    let error = null;
    const body = {
      idempotencyKey: idempotencyKey,
      routeName: this.unitService.routeName(),
    };
    let url = `${environment.pubQueuePaymentApiUrl}/v1/idempotency/verify/v2`;
    while (!finished && this.retryingEnabled()) {
      try {

        resp = await firstValueFrom(this.http.post<PaymentV2Response>(url, body));
        error = null;
        if (resp.success || resp.error) {
          console.log("Verify done.")
          finished = true;
        }
      } catch (e) {
        error = e;
      }
      retry++;
      if (retry > _retryMaxCount || finished) { // Wait max for 2 minutes
        finished = true;
      } else {
        console.log("Retry in 1sec (" + retry + `/${_retryMaxCount})...`)
        await this.delay(1000);
      }
    }
    //this.retryingEnabled = false;
    if (error) {
      resp = new PaymentV2Response;
      resp.error = {
        showResponseText: false,
        languageCode: 'SWISH_ERROR_CONNECTION',
        responseText: error instanceof Error ? error.message : 'Unknown error',
        resetOrder: true
      }
    }
    else if (retry > _retryMaxCount && _retryMaxCount !== 0) {
      resp = new PaymentV2Response;
      resp.error = {
        showResponseText: false,
        languageCode: 'SWISH_VERIFY_TIMEOUT',
        responseText: '',
      }
    }
    return resp;
  }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async charge(idempotencyKey: string, routeName: string): Promise<any> {
    const body = {
      idempotencyKey: idempotencyKey,
      routeName: routeName,
    };
    const url = `${environment.pubQueuePaymentApiUrl}/v1/idempotency/charge`;
    const response = await firstValueFrom(this.http.post<any>(url, body));
    return response;
  }

  async getUserReceiptsLastMonth(userId: string): Promise<any[]> {
    try {
      if (!userId) {
        return [];
      }

      const { startSeconds, endSeconds } = this.getLastMonthDateRange();
      const response: any = await firstValueFrom(
        this.getfilteredPurchaseHistory(userId, startSeconds, endSeconds)
      );

      if (response && Array.isArray(response)) {
        return response;
      }
      return [];
    } catch {
      return [];
    }
  }

  getfilteredPurchaseHistory(id: string, startDate: number, endDate: number): Observable<any> {
    let url = `${environment.dashboardApiUrl}/v1/receipts/${id}?startAt=${startDate}&endAt=${endDate}`;
    return this.http.get<any>(url);
  }

  resetOrderStreams(): void {
    for (const stream of Object.keys(this.orderUnsubscribes) as Array<'active' | 'previous'>) {
      this.orderUnsubscribes[stream]?.();
      this.orderUnsubscribes[stream] = null;
    }
    this.activeOrders.set([]);
    this.previousOrders.set([]);
    this.activeOrdersLoading.set(false);
    this.previousOrdersLoading.set(false);
    this.resetReceipts();
  }

  subscribeOrderStream(stream: 'active' | 'previous', userId: string | undefined | null): void {
    const orders  = stream === 'active' ? this.activeOrders  : this.previousOrders;
    const loading = stream === 'active' ? this.activeOrdersLoading : this.previousOrdersLoading;

    if (this.orderUnsubscribes[stream]) {
      return;
    }

    if (!userId) {
      orders.set([]);
      loading.set(false);
      return;
    }

    loading.set(true);

    const routeName = this.routeName() || this.unitService.routeName();
    if (!routeName) {
      orders.set([]);
      loading.set(false);
      return;
    }

    const db = getFirestore();
    const colRef = collection(db, `pos/${routeName}/catering_orders`);
    const q = query(
      colRef,
      where('userId', '==', userId),
      where('status', 'in', [...this.ORDER_STATUSES[stream]]),
      orderBy('dateISO', 'desc')
    );
    this.orderUnsubscribes[stream] = onSnapshot(
      q,
      (snapshot) => {
        orders.set(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        loading.set(false);
      },
      () => {
        orders.set([]);
        loading.set(false);
      }
    );
  }

  async loadReceiptsForUser(userId: string | undefined | null): Promise<void> {
    if (this.receiptsLoaded() || this.receiptsLoading()) return;
    if (!userId) {
      this.receipts.set([]);
      return;
    }
    this.receiptsLoading.set(true);
    try {
      const receipts = await this.getUserReceiptsLastMonth(userId);
      this.receipts.set(receipts);
      this.receiptsLoaded.set(true);
    } finally {
      this.receiptsLoading.set(false);
    }
  }

  resetReceipts(): void {
    this.receipts.set([]);
    this.receiptsLoaded.set(false);
    this.receiptsLoading.set(false);
  }
}
